/**
 * ChordIndicator
 *
 * Displays a small pill at bottom-right when a chord prefix is active.
 * Shows "g …" with a 1000ms countdown auto-dismiss.
 *
 * Per STREAM_AESTHETIC.md §6 toast conventions.
 */

import { useEffect, useState } from "react";
import { useShortcutStore } from "@/shared/stores/shortcut-store";

export function ChordIndicator() {
  const chordPrefix = useShortcutStore((s) => s.chordPrefix);
  const chordPrefixTimestamp = useShortcutStore((s) => s.chordPrefixTimestamp);
  const clearChordPrefix = useShortcutStore((s) => s.clearChordPrefix);

  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (!chordPrefix) return;

    // Auto-clear after 1000ms
    const timeout = setTimeout(() => {
      clearChordPrefix();
    }, 1000);

    // Countdown animation
    const interval = setInterval(() => {
      const elapsed = Date.now() - chordPrefixTimestamp;
      const remaining = Math.max(0, 100 - (elapsed / 1000) * 100);
      setProgress(remaining);
    }, 16); // ~60fps

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [chordPrefix, chordPrefixTimestamp, clearChordPrefix]);

  if (!chordPrefix) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: "20px",
        right: "20px",
        background: "var(--bg-elevated)",
        border: "1px solid var(--border-default)",
        borderRadius: "var(--radius-md)",
        padding: "8px 12px",
        boxShadow: "var(--shadow-md)",
        fontSize: "13px",
        fontWeight: "500",
        color: "var(--text-primary)",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        zIndex: 9999,
      }}
    >
      <span>{chordPrefix} …</span>
      <div
        style={{
          width: "32px",
          height: "2px",
          background: "var(--border-subtle)",
          borderRadius: "2px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${progress}%`,
            height: "100%",
            background: "var(--accent-primary)",
            transition: "width 16ms linear",
          }}
        />
      </div>
    </div>
  );
}
